import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Info, CheckCircle, AlertTriangle, XCircle } from 'lucide-react'

/**
 * Fluent 2 MessageBar/Alert Component
 * https://fluent2.microsoft.design/components/web/react/core/messagebar/usage
 * Intents: info (brand blue), success (green), warning (orange), error (red)
 */
const alertVariants = cva(
  'relative w-full rounded-md border-l-4 p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:h-5 [&>svg]:w-5',
  {
    variants: {
      variant: {
        default:
          'bg-[#f0f6ff] border-l-[#0c00ff] text-neutral-900 [&>svg]:text-[#0c00ff] dark:bg-[#0c00ff]/10 dark:border-l-[#479ef5] dark:text-neutral-100 dark:[&>svg]:text-[#479ef5]',
        info:
          'bg-[#f0f6ff] border-l-[#0c00ff] text-neutral-900 [&>svg]:text-[#0c00ff] dark:bg-[#0c00ff]/10 dark:border-l-[#479ef5] dark:text-neutral-100 dark:[&>svg]:text-[#479ef5]',
        success:
          'bg-[#f1faf1] border-l-[#0e7a0d] text-neutral-900 [&>svg]:text-[#0e7a0d] dark:bg-[#0e7a0d]/10 dark:border-l-[#54b054] dark:text-neutral-100 dark:[&>svg]:text-[#54b054]',
        warning:
          'bg-[#fff9f5] border-l-[#9d5d00] text-neutral-900 [&>svg]:text-[#9d5d00] dark:bg-[#9d5d00]/10 dark:border-l-[#f9a825] dark:text-neutral-100 dark:[&>svg]:text-[#f9a825]',
        destructive:
          'bg-[#fdf3f4] border-l-[#c50f1f] text-neutral-900 [&>svg]:text-[#c50f1f] dark:bg-[#c50f1f]/10 dark:border-l-[#dc626d] dark:text-neutral-100 dark:[&>svg]:text-[#dc626d]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

const variantIcons = {
  default: Info,
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  destructive: XCircle,
}

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  showIcon?: boolean
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = 'default', showIcon = true, children, ...props }, ref) => {
    const Icon = variantIcons[variant || 'default']
    return (
      <div
        ref={ref}
        role="alert"
        className={cn(alertVariants({ variant }), className)}
        {...props}
      >
        {showIcon && <Icon />}
        {children}
      </div>
    )
  }
)
Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('mb-1 font-semibold leading-tight text-sm', className)}
    {...props}
  />
))
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm leading-relaxed [&_p]:leading-relaxed', className)}
    {...props}
  />
))
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertTitle, AlertDescription, alertVariants }